import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Skeleton,
  BadgeAlt as Badge,
} from '@cargomatic/ui';
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from 'utils/ui/classes';
import formatMoney from 'utils/formatters/Money';
import { sluggify } from 'utils/localization/utils';
import COLUMNS from './JobListColumns';

const DRAYAGE = 'drayage';
const ORDER_LEVEL_KEY = '__order_level__';

const ChargeRow = ({ charge, t }) => {
  const message =
    charge.type === 'customer_adjustment'
      ? titleize(charge.message ?? '')
      : t(sluggify(charge.message ?? ''));

  return (
    <tr className="border-b border-border/50">
      <td className="py-1.5 px-3 text-sm text-muted-foreground">{message}</td>
      <td className="py-1.5 px-3 text-sm text-right">
        <span className={cn(charge.value < 0 && 'text-destructive')}>
          {formatMoney(charge.value, charge.currency)}
        </span>
      </td>
    </tr>
  );
};

const JobList = ({
  context,
  ledgerId,
  ledgerType,
  jobs,
  lineItems,
  isLoading = true,
  isError,
}) => {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState({});

  const lineItemLookup = useMemo(() => {
    if (!lineItems || lineItems.length === 0) return {};
    const ledgerProperty = context === 'invoice' ? 'receivables' : 'payables';
    const isShorthaul = ledgerType !== DRAYAGE;
    return lineItems.reduce((data, job) => {
      const key = job._id ? job._id.toString() : ORDER_LEVEL_KEY;
      const items = (job[ledgerProperty] ?? []).map((item) => {
        return { ...item, needs_equipment: !isShorthaul && !item.equipment };
      });
      data[key] = (data[key] ?? []).concat(items);
      return data;
    }, {});
  }, [lineItems, context, ledgerType]);

  const columns = COLUMNS[ledgerType] || [];

  const groupedJobs = useMemo(() => {
    if (!jobs) return [];
    const sorted = sortBy(jobs, [
      (job) => (job.shipment_id ? 1 : 0),
      'driverName',
      'jobDeliverBy',
    ]);
    const ledgerProperty = context === 'invoice' ? 'invoice_id' : 'payout_id';

    const result = [];
    sorted.forEach((job) => {
      const lookupKey = job.shipment_id || ORDER_LEVEL_KEY;
      const items = lineItemLookup[lookupKey] || [];
      const currentItems = items.filter(
        (item) => item[ledgerProperty] === ledgerId,
      );

      if (!job.shipment_id) {
        currentItems.forEach((charge) => {
          result.push({
            job,
            charges: [charge],
            total: parseFloat(charge.value) || 0,
            isOrderLevel: true,
          });
        });
      } else {
        const total = currentItems.reduce(
          (sum, item) => sum + (parseFloat(item.value) || 0),
          0,
        );
        result.push({ job, charges: currentItems, total });
      }
    });
    return result;
  }, [jobs, lineItemLookup, context, ledgerId]);

  const toggleRow = (shipmentId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [shipmentId]: !prev[shipmentId],
    }));
  };

  const localizeTitles = (column) => {
    let display_text = t(column.title, {
      count: column?.count,
    });
    if (column.titleize) display_text = titleize(display_text);
    return (
      <TableHead
        data-testid="p-be4e0d7d"
        key={column.field}
        className={column.className}
      >
        {display_text}
      </TableHead>
    );
  };

  if (!jobs && !isLoading) return null;

  return (
    <div data-testid="div-7df63e75" className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>{columns.map((column) => localizeTitles(column))}</TableRow>
        </TableHeader>
        <TableBody>
          {isError ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="py-8">
                <div className="flex items-center justify-center gap-2 text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  <span>{t('FAILED_TO_LOAD_LINE_ITEMS')}</span>
                </div>
              </TableCell>
            </TableRow>
          ) : isLoading && !isError ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`skel-${i}`}>
                {Array.from({ length: columns.length || 6 }).map((_, j) => (
                  <TableCell key={`skel-${i}-${j}`}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : groupedJobs.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center text-muted-foreground py-8"
              >
                {t('NO_DATA_FOUND')}
              </TableCell>
            </TableRow>
          ) : (
            groupedJobs.map(
              ({ job, charges, total, isOrderLevel }, groupIndex) => {
                if (isOrderLevel) {
                  const charge = charges[0];
                  const chargeMessage =
                    charge.type === 'customer_adjustment'
                      ? titleize(charge.message ?? '')
                      : t(sluggify(charge.message ?? ''));
                  return (
                    <TableRow
                      key={`order-${charge._id}`}
                      data-testid={`div-644b2292-${groupIndex}`}
                    >
                      {columns.map((column, index) => {
                        const key = `${column.field}-${charge._id}`;
                        if (index === 0) {
                          return (
                            <TableCell
                              data-testid={`div-4e0516cc-${index}`}
                              key={key}
                              className={column.className}
                            >
                              <div className="flex items-center gap-2">
                                {chargeMessage}
                                <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200">
                                  Order-Level
                                </Badge>
                              </div>
                            </TableCell>
                          );
                        }
                        if (column.field === 'total') {
                          return (
                            <TableCell
                              data-testid={`div-4e0516cc-${index}`}
                              key={key}
                              className={column.className}
                            >
                              {formatMoney(total, charge.currency)}
                            </TableCell>
                          );
                        }
                        if (column.field === 'order_id') {
                          return (
                            <TableCell
                              data-testid={`div-4e0516cc-${index}`}
                              key={key}
                              className={column.className}
                            >
                              {job.order_id}
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell
                            data-testid={`div-4e0516cc-${index}`}
                            key={key}
                            className={column.className}
                          />
                        );
                      })}
                    </TableRow>
                  );
                }

                const isExpanded = expandedRows[job.shipment_id];
                return (
                  <React.Fragment key={job.shipment_id || job._id}>
                    <TableRow
                      data-testid={`div-644b2292-${groupIndex}`}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => toggleRow(job.shipment_id)}
                    >
                      {columns.map((column, index) => {
                        const key = `${column.field}-${job._id}`;
                        if (index === 0) {
                          return (
                            <TableCell
                              data-testid={`div-4e0516cc-${index}`}
                              key={key}
                              className={column.className}
                            >
                              <div className="flex items-center gap-2">
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 shrink-0" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 shrink-0" />
                                )}
                                {column.value(job, total)}
                              </div>
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell
                            data-testid={`div-4e0516cc-${index}`}
                            key={key}
                            className={column.className}
                          >
                            {column.value(job, total)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {isExpanded && charges.length > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="p-0 bg-muted/30"
                        >
                          <div className="py-2 px-8">
                            <table className="w-auto min-w-[320px]">
                              <thead>
                                <tr className="border-b border-border">
                                  <th className="py-1.5 px-3 text-left text-xs font-medium text-muted-foreground uppercase">
                                    {t('CHARGE')}
                                  </th>
                                  <th className="py-1.5 px-3 text-right text-xs font-medium text-muted-foreground uppercase">
                                    {t('AMOUNT')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {charges.map((charge, idx) => (
                                  <ChargeRow
                                    key={charge._id || idx}
                                    charge={charge}
                                    t={t}
                                  />
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              },
            )
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default JobList;

JobList.propTypes = {
  context: PropTypes.string.isRequired,
  ledgerType: PropTypes.string.isRequired,
  ledgerId: PropTypes.string.isRequired,
  jobs: PropTypes.array.isRequired,
  lineItems: PropTypes.array,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
};
